import React, { useState } from 'react';
import { Button, Badge, Tooltip, Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { FavoriteBorder as HeartIcon, Favorite as HeartFilledIcon } from '@mui/icons-material';

// Create a pulse animation
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(229, 57, 53, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(229, 57, 53, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(229, 57, 53, 0);
  }
`;

// Styled button with animation
const AnimatedButton = styled(Button)(({ theme, animate, variant }) => ({
  borderRadius: 20,
  padding: variant === 'compact' ? '6px 12px' : '8px 16px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  backgroundColor: '#e53935',
  color: 'white',
  '&:hover': {
    backgroundColor: '#c62828',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
  '&::after': animate ? {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    width: '100%',
    height: '100%',
    top: '0',
    left: '0',
    animation: `${pulse} 2s infinite`,
  } : {},
}));

// Badge style
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#FFD700',
    color: '#000',
    fontWeight: 'bold',
    fontSize: '0.65rem',
    padding: '0 4px',
  },
}));

/**
 * Reusable Support Button Component
 * @param {Object} props - Component props
 * @param {string} props.variant - 'default', 'compact', or 'icon-only'
 * @param {boolean} props.animate - Whether to show the pulse animation
 * @param {boolean} props.showBadge - Whether to show the "New" badge
 * @param {string} props.tooltip - Optional tooltip text
 * @param {Object} props.sx - Additional MUI sx props for styling
 * @param {string} props.customText - Optional custom button text
 */
const SupportButton = ({
  variant = 'default',
  animate = false,
  showBadge = false,
  tooltip = 'Support Hay Day Tools',
  sx = {},
  customText,
}) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    navigate('/donate');
  };

  // Choose content based on variant
  let buttonContent;
  if (variant === 'icon-only') {
    buttonContent = isHovered ? <HeartFilledIcon /> : <HeartIcon />;
  } else {
    buttonContent = (
      <>
        {isHovered ? <HeartFilledIcon sx={{ mr: 1 }} /> : <HeartIcon sx={{ mr: 1 }} />}
        {customText || (variant === 'compact' ? 'Support' : 'Support Us')}
      </>
    );
  }

  // Wrap with Badge if showBadge is true
  const buttonWithOptionalBadge = showBadge ? (
    <StyledBadge badgeContent="New" overlap="circular">
      {buttonContent}
    </StyledBadge>
  ) : buttonContent;

  return (
    <Tooltip title={tooltip} arrow>
      <Box sx={{ display: 'inline-block', ...sx }}>
        <AnimatedButton
          variant={variant === 'icon-only' ? 'circular' : 'contained'}
          animate={animate}
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          aria-label="Support Hay Day Tools"
          sx={{
            minWidth: variant === 'icon-only' ? '40px' : 'auto',
            width: variant === 'icon-only' ? '40px' : 'auto',
            height: variant === 'icon-only' ? '40px' : 'auto',
            p: variant === 'icon-only' ? '8px' : undefined,
            borderRadius: variant === 'icon-only' ? '50%' : '20px',
            ...sx
          }}
        >
          {buttonWithOptionalBadge}
        </AnimatedButton>
      </Box>
    </Tooltip>
  );
};

export default SupportButton;